/**
 * 작성자 : 한영광
 * 날짜 : 2025.06.02
 * 경로 : 출역 관리 - 방문자 현황
 */

import { useEffect, useState, useRef, RefObject } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { IoChevronUpSharp } from 'react-icons/io5';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ERROR, LIST_COUNT } from '../../../_constants';
import { IUser, pageInfoState, userState } from '../../../atoms';
import { IComCdList, ITabObject } from 'customTypes';
import { BtnGhost } from '../../../components/Button';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { FloatingButtonToTop } from '../../../assets/styles/FloatingButtonToTop';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { useSetAuth } from '../../../utils/useSetAuth';
import { trimObject } from '../../../utils/trimObject';
import { todayYYYYMMDD } from '../../../utils/formatDate';
import { scrollToNodeTop } from '../../../utils/scrollToNodeTop';
import { setComCdListState } from '../../../utils/setComCdListState';
import { useDetectScrolledToBottom } from '../../../utils/useDetectScrolledToBottom';
import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import Tab from '../../../components/Tab';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import TuiGrid from '../../../components/Table/TuiGrid';
import SelectBox from '../../../components/SelectBox';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import { logPost } from '../../../services/log';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import i18n from '../../../translation/i18n';
import useSetListCount from '../../../utils/useSetListCount';
import { apiGet } from '../../../services/_common';
import ShortcutButton from '../../../components/button/ShortcutButton';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../../utils/deviceAuth';
import ImageBtnRenderer from '../../../components/ImageBtnRenderer';
import Thumbnail from '../../../components/Modal/Thumbnail';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem 0;

  > :nth-child(1) {
    align-self: flex-start;
  }
`;

const VisitEdu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const location = useLocation();
  const size = useOutletContext<any>();
  const tabInfo = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const toDay = todayYYYYMMDD();
  const [userInfo, setUserInfo] = useRecoilState<IUser>(userState);
  const { userMenuList, eExcelYn, eListYn, ePrintYn } = userInfo;
  const [tableState, setTableState] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const vNameRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [rangeState, setRangeState] = useState([
    {
      startDate: location.state?.startDate ? new Date(location.state?.startDate) : new Date(),
      endDate: location.state?.endDate ? new Date(location.state?.endDate) : new Date(),
      key: 'selection',
    },
  ]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [searchOption, setSearchOption] = useState({
    vName: location.state?.vName || '',
    start: location.state?.startDate || toDay,
    end: location.state?.endDate || toDay,
  });
  const [excelBtn, setExcelBtn] = useState(false);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: { wCd: '' }, index: 0 });
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const [rowKey, setRowKey] = useState('');
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initiate, setinitiate] = useState('');
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const showExcelBtn = showExcelButton(auth.excelAuth);

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    setLoading(true);
    getVisitAPI().then((res: any) => {
      if (res.status === 200) {
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '방문자 교육 현황',
          action: '조회',
          etc: ``,
        });
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    setColumns([
      {
        header: t('서명'),
        name: 'eduSign',
        align: 'center',
        renderer: {
          type: ImageBtnRenderer,
          options: {
            onImgButtonClicked,
          },
        },
      },
      {
        header: t('방문자명'),
        name: 'vName',
        align: 'left',
        isPrivate: true,
        maskingType: 'name',
        sortable: true,
        width: 150,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('연락처'),
        name: 'vTel',
        isPrivate: true,
        maskingType: 'tel',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('교육일자'),
        name: 'eduDate',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
    ]);
  }, [tableState, i18n.language]);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  // 방문자 현황 조회 API
  const getVisitAPI = async () => {
    const newSearchOption = {
      ...searchOption,
      aDate1: searchOption.start.replaceAll('-', ''),
      aDate2: searchOption.end.replaceAll('-', ''),
    };
    const { start, end, ...finalSearchObj } = newSearchOption; // searchOption에서 start, end값 삭제
    const trimData = trimObject(finalSearchObj);
    const req = { ...trimData, hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/visit/edu', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setTableState(data);
    } else {
      toast.error(t(ERROR));
    }
    setLoading(false);
    return res;
  };

  // 테이블 안에 이미지 버튼 클릭
  const onImgButtonClicked = (index: number) => {
    const { eduSign = '' } = tableState[index];
    setOpenModal({
      status: true,
      type: 'imgView',
      data: { aImgPath: eduSign, style: { width: '15vw', height: '10vw' } },
      thumbnailOnly: true,
    });
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    setRowKey('');
    if (!openModal.status || (openModal.status && openModal.type === 'filter')) {
      if (!searchOption.end && !searchOption.start) {
        toast.warning(t('출역일자를 입력하세요'));
      } else if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('출역일자를 다시 입력하세요'));
        setSearchOption((prev: any) => ({ ...prev, end: '' }));
      } else {
        setLoading(true);
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        setVisibleRangePicker(false);
        navigate({
          pathname: location.pathname,
          search: '',
        });
        getVisitAPI();
      }
    }
  };

  // const initiateSearchOptions = () => {
  //   setSjCd({ type: 'sjCd', sjCd: '', cdName: '' });
  //   setFNum(INIT_F_NUM);
  //   setWPrejobtype({ type: 'wPrejobtype', wPrejobtype: '', cdName: '' });
  //   setWJobtype({ type: 'wJobtype', wJobtype: '', cdName: '' });
  //   setFLocation(INIT_F_LOCATION);
  // };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    if (location.state?.sjName) {
      location.state.sjName = '';
    }
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev) => ({ ...prev, [el]: '', start: toDay, end: toDay }));
    });
    setinitiate(`${Math.random()}`);
    // initiateSearchOptions();
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '방문자 현황',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  // 방문자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
      // return <BtnLightBlue onClick={onClickExcelSave}>엑셀 저장</BtnLightBlue>;
    }
    return null;
  };

  const renderPrintBtn = () => {
    if (auth.printAuth) {
      return (
        <BtnGhost onClick={onClickPrint}>
          <span className='material-symbols-rounded'>print</span>
          {t('인쇄')}
        </BtnGhost>
      );
    }
    return null;
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickTab = (tab: string) => {
    setCurrentTabMrCd(tab);
  };

  // 근로자명 입력창에서 엔터입력시 검색
  const loginOnEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (vNameRef.current) onClickSearch();
    }
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  return (
    <div className='contents'>
      <div className='content-container oneColumn'>
        <Root loading={loading}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                {isBottom && (
                  <FloatingButtonToTop>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                      <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                    </button>
                  </FloatingButtonToTop>
                )}
                <div className='inputsWrapper'>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('방문일자')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper' style={{ left: '20%' }}>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('방문자명')}
                        label=''
                        type='text'
                        id='vName'
                        name='vName'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={vNameRef}
                        onKeyDown={loginOnEnterKeyDown}
                        className='textInput'
                      />
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{tableState.length}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount[LIST_COUNT]}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>
                    <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('근로자명')}
                        label=''
                        type='text'
                        id='vName'
                        name='vName'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={vNameRef}
                        onKeyDown={loginOnEnterKeyDown}
                        ico='search'
                      />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                </div>
              </div>
            )}
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              filterTableState={filterTableState}
              setFilterTableState={setFilterTableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
              usePagenation
              onClickRow={onClickRow}
              scrollX
              height={tuiHeight}
              eListYn={eListYn}
              eExcelYn={eExcelYn}
              rowKey={rowKey}
              frozenCount={frozenCount}
              setRowKey={setRowKey}
            />
          </TuiGridWrapper>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>{openModal.status && openModal.type === 'imgView' && <Thumbnail openModal={openModal} setOpenModal={setOpenModal} />}</Portal>

      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('출역일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default VisitEdu;
